import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getToken } from './tokenStorage';
import { getConfigVar } from '../config';

// Get the service URL from runtime config first else current origin
const SERVICE_URL = getConfigVar('REACT_APP_SERVICE_URL', window.location.origin)!;

/**
 * Cache configuration options
 */
export interface CacheConfig {
  /** Whether caching is enabled (default: true) */
  enabled?: boolean;
  /** Time to live in milliseconds (default: 5 minutes) */
  ttl?: number;
  /** Maximum number of cache entries (default: 100) */
  maxSize?: number;
}

/**
 * Configuration for creating API client instances
 */
export interface ApiClientConfig extends AxiosRequestConfig {
  baseURL?: string;
  /** Cache configuration */
  cache?: CacheConfig;
}

/**
 * A simple in-memory cache for API responses
 */
class ApiCache {
  private cache: Map<string, { data: any; timestamp: number }>;
  private config: Required<CacheConfig>;

  constructor(config: CacheConfig = {}) {
    this.cache = new Map();
    this.config = {
      enabled: config.enabled !== undefined ? config.enabled : true,
      ttl: config.ttl || 0, // 5-minute default
      maxSize: config.maxSize || 100,
    };
  }

  /**
   * Generate a cache key from the request
   */
  private generateKey(config: AxiosRequestConfig): string {
    const { url, method, params, data } = config;
    return `${method || 'get'}-${url}-${JSON.stringify(params || {})}-${JSON.stringify(data || {})}`;
  }

  /**
   * Check if a cache entry is valid (not expired)
   */
  private isValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.config.ttl;
  }

  /**
   * Get a value from the cache
   */
  get(config: AxiosRequestConfig): AxiosResponse | null {
    if (!this.config.enabled || config.method !== 'get') {
      return null;
    }

    const key = this.generateKey(config);
    const entry = this.cache.get(key);

    if (entry && this.isValid(entry.timestamp)) {
      console.log(`Cache hit for ${key}`);
      return entry.data;
    }

    if (entry) {
      // Remove expired entry
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Set a value in the cache
   */
  set(config: AxiosRequestConfig, response: AxiosResponse): void {
    if (!this.config.enabled || config.method !== 'get') {
      return;
    }

    const key = this.generateKey(config);

    // Enforce cache size limit
    if (this.cache.size >= this.config.maxSize) {
      // Remove the oldest entry
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      data: response,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear the entire cache or entries matching a URL pattern
   */
  clear(urlPattern?: string): void {
    if (!urlPattern) {
      this.cache.clear();
      console.log('Cache cleared');
      return;
    }

    // Clear entries matching the URL pattern
    for (const key of this.cache.keys()) {
      if (key.includes(urlPattern)) {
        this.cache.delete(key);
        console.log(`Cache entry matching ${urlPattern} cleared`);
      }
    }
  }
}

/**
 * Creates and configures an Axios instance with interceptors for consistent
 * request/response handling and error management.
 */
export const createApiClient = (config: ApiClientConfig = {}): AxiosInstance => {
  const apiClient = axios.create({
    baseURL: SERVICE_URL,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
    },
    ...config,
  });

  // Create a cache instance
  const cache = new ApiCache(config.cache);

  // Add cache property to the client
  (apiClient as any).cache = cache;

  // Request interceptor
  apiClient.interceptors.request.use(
    (config) => {
      // Check the cache for GET requests
      if (config.method === 'get') {
        const cachedResponse = cache.get(config);
        if (cachedResponse) {
          // Create a new Promise that resolves with the cached response
          const source = axios.CancelToken.source();
          config.cancelToken = source.token;
          setTimeout(() => {
            source.cancel(cachedResponse as any);
          }, 0);
        }
      }

      // Add authorization token to request if available
      const token = getToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      } else if (config.url && !config.url.includes('/auth') && !config.url.includes('/login')) {
        // For non-auth endpoints, if the token is not available, add a small delay
        // to give time for the token to be set (helps with race conditions during app initialization)
        console.warn(`Token not available for request to ${config.url}, adding delay`);
        return new Promise((resolve) => {
          setTimeout(() => {
            // Try to get token again after delay
            const delayedToken = getToken();
            if (delayedToken) {
              config.headers = config.headers || {};
              config.headers['Authorization'] = `Bearer ${delayedToken}`;
              console.log(`Token obtained after delay for ${config.url}`);
            } else {
              console.warn(`Token still not available after delay for ${config.url}`);
            }
            resolve(config);
          }, 1000);
        });
      }

      // You can modify the request config here (add headers, auth tokens, etc.)
      return config;
    },
    (error) => {
      console.error('Request error:', error);
      return Promise.reject(error);
    },
  );

  // Response interceptor
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Cache successful GET responses
      if (response.config.method === 'get') {
        cache.set(response.config, response);
      }

      // Clear the entire cache on successful mutation operations to force fresh fetching
      if (
        response.config.method &&
        ['post', 'put', 'delete', 'patch'].includes(response.config.method)
      ) {
        // Clear the entire cache to ensure fresh data is fetched after updates
        cache.clear();
        console.log(`Cache cleared after ${response.config.method.toUpperCase()} operation`);
      }

      // You can modify the response data here before it reaches the component
      return response;
    },
    (error: AxiosError) => {
      // If the error was caused by the cache cancellation, return the cached response
      if (axios.isCancel(error)) {
        return Promise.resolve(error.message);
      }

      // Clear the entire cache on mutation operations even if they fail
      if (error.config && ['post', 'put', 'delete', 'patch'].includes(error.config.method || '')) {
        // Clear the entire cache to ensure consistency
        cache.clear();
        console.log(`Cache cleared after failed ${error.config.method?.toUpperCase()} operation`);
      }

      // Handle different error scenarios
      if (error.response) {
        // The request was made, and the server responded with a status code
        // that falls out of the range of 2xx
        const status = error.response.status;

        switch (status) {
          case 401:
            // Handle unauthorized errors (e.g., redirect to login)
            console.error('Unauthorized access:', error.response.data);
            // Could dispatch an action to clear auth state and redirect
            break;
          case 403:
            // Handle forbidden errors
            console.error('Forbidden access:', error.response.data);
            break;
          case 404:
            // Handle not found errors
            console.error('Resource not found:', error.response.data);
            break;
          case 500:
            // Handle server errors
            console.error('Server error:', error.response.data);
            break;
          default:
            // Handle other errors
            console.error(`Error with status ${status}:`, error.response.data);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
      }

      // You can transform the error or add additional information
      return Promise.reject(error);
    },
  );

  return apiClient;
};

// Create and export a default API client instance
export const apiClient = createApiClient();

/**
 * Creates an Axios instance for pre-auth public endpoints (invitation
 * accept, consent give, etc.). No auth interceptor, no cache — these
 * flows are reached from email links before the user is signed in, so
 * the standard apiClient's token-delay loop would only slow them down.
 */
export const createPublicApiClient = (config: AxiosRequestConfig = {}): AxiosInstance =>
  axios.create({
    baseURL: SERVICE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
    ...config,
  });

export const publicApiClient = createPublicApiClient();

/**
 * Cache control interface for the API client
 */
export interface ApiCacheControl {
  /**
   * Clear the entire cache or entries matching a URL pattern
   * @param urlPattern Optional URL pattern to match
   */
  clear(urlPattern?: string): void;

  /**
   * Get the current cache configuration
   */
  getConfig(): Required<CacheConfig>;

  /**
   * Update the cache configuration
   * @param config New cache configuration
   */
  updateConfig(config: Partial<CacheConfig>): void;
}

/**
 * Get the cache control interface for the API client
 * @param client The API client instance
 */
export const getCacheControl = (client: AxiosInstance): ApiCacheControl => {
  const cache = (client as any).cache;
  if (!cache) {
    throw new Error('Cache not available on this API client');
  }

  return {
    clear: (urlPattern?: string) => cache.clear(urlPattern),
    getConfig: () => ({ ...cache.config }),
    updateConfig: (config: Partial<CacheConfig>) => {
      cache.config = {
        ...cache.config,
        ...config,
      };
    },
  };
};
